/* Copyright (c) 2020-present CloudIO Inc */

import {
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { StringProp } from 'componentregistry';
import { isUUID } from 'components/Popup/TreePopup';
import Icon from 'icons/Icon';
import { PlatformContext } from 'platform/PlatformProvider';
import React, { memo, useContext, useId, useState } from 'react';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  icon: {
    color: theme.palette.primary.main,
  },
}));

function ItemIDInput({
  activeItemId,
  def,
  pageId,
  appUid,
  property,
  isViewOnlyUser = false,
}: PropertyEditorProps<StringProp, string>) {
  const classes = useStyles();

  const [value, setValue] = useState(activeItemId);
  const [error, setError] = useState('');
  React.useEffect(() => {
    setValue(activeItemId);
  }, [activeItemId]);
  const platform = useContext(PlatformContext);
  const id = useId();
  const mgr = platform.getManager(activeItemId);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setValue(e.target.value);
  }
  function onBlur() {
    if (!value) {
      setValue(activeItemId);
    } else if (activeItemId !== value) {
      if (value.length < 3) {
        setError('Item ID must have atleast 3 chars!');
      } else {
        setError(platform.updateItemID(appUid, pageId, activeItemId, value));
      }
    } else if (error) {
      setError('');
    }
  }
  function shortenID() {
    platform.shortenItemID(appUid, pageId, activeItemId);
  }

  return (
    <FormControl
      disabled={isViewOnlyUser}
      data-testid={property}
      className={classes.root}
      classes={{ root: classes.input }}
      fullWidth
      variant="standard"
    >
      <InputLabel htmlFor={id}>{def.label}</InputLabel>
      <Input
        id={id}
        spellCheck={false}
        autoCorrect="off"
        error={!!error}
        disabled={mgr?.isItemIdReadOnly() || isViewOnlyUser}
        fullWidth
        onFocus={(e) => {
          e.target.select();
        }}
        onBlur={onBlur}
        type="text"
        value={value ?? ''}
        onChange={onChange}
        endAdornment={
          isUUID(value) ? (
            <InputAdornment position="end">
              <IconButton
                disabled={isViewOnlyUser}
                onClick={shortenID}
                data-tip="Shorten"
              >
                <Icon
                  disabled={isViewOnlyUser}
                  className={classes.icon}
                  iconName="wand-magic"
                />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
      />
      <FormHelperText error={!!error}>{error}</FormHelperText>
    </FormControl>
  );
}

export default memo(ItemIDInput);
